import { Component, OnInit, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { HttpErrorResponse } from '@angular/common/http';
import { forkJoin } from 'rxjs';
import { AdminService } from '../../services/AdminService/admin';
import { CountryEventTemplateResponse, CountryTermRuleResponse } from '../../models/country-config.dto';
import { extractErrorMessage } from '../../shared/error-utils';
import { ErrorBanner } from '../../shared/components/error-banner/error-banner';
import { EmptyState } from '../../shared/components/empty-state/empty-state';
import { findCountry } from '../../shared/countries';
import {
  Raw,
  TemplateError,
  analyse,
  isWellFormed,
  isWellFormedDocumentOrFragment,
  render,
} from '../../shared/xml-template-engine';

/** Whole envelope with its events spliced in, or one event fragment on its own. */
export type TesterMode = 'document' | 'fragment';

export interface DraftEvent {
  key: number;
  eventType: string;
  /** term -> single value. */
  scalars: Record<string, string>;
  /** term -> one entry per line, for the terms a template reaches with {{#each}}. */
  lists: Record<string, string>;
}

export interface TesterResult {
  xml: string | null;
  problemCode: string | null;
  problem: string | null;
  unresolved: string[];
  withheld: string[];
}

/** The document template's fixed variables the sandbox lets you type. The other two are derived. */
const EDITABLE_DOCUMENT_VARIABLES = ['epc', 'exporterCompanyId', 'importerCompanyId', 'payloadHash'];

/** Carries a problemCode the way the server's ApiException does, so reporting reads the same. */
class Problem extends Error {
  constructor(readonly code: string, message: string) {
    super(message);
  }
}

@Component({
  selector: 'app-xml-template-tester',
  imports: [CommonModule, FormsModule, ErrorBanner, EmptyState],
  templateUrl: './xml-template-tester.html',
  styleUrl: './xml-template-tester.scss',
})
export class XmlTemplateTester implements OnInit {
  templates = signal<CountryEventTemplateResponse[]>([]);
  rules = signal<CountryTermRuleResponse[]>([]);
  selectedCountry = signal('');
  mode = signal<TesterMode>('document');

  isLoading = signal(false);
  errorMessage = signal('');
  result = signal<TesterResult | null>(null);

  docValues: Record<string, string> = {};
  events: DraftEvent[] = [];
  private nextKey = 1;

  constructor(private admin: AdminService) {}

  ngOnInit(): void {
    this.isLoading.set(true);
    this.errorMessage.set('');

    forkJoin({
      templates: this.admin.listEventTemplates(),
      rules: this.admin.listTermRules(),
    }).subscribe({
      next: (res) => {
        this.isLoading.set(false);
        this.templates.set(res.templates.data);
        this.rules.set(res.rules.data);
        const first = this.availableCountries[0];
        if (first) {
          this.selectCountry(first);
        }
      },
      error: (err: HttpErrorResponse) => {
        this.isLoading.set(false);
        this.errorMessage.set(extractErrorMessage(err, 'Failed to load the country templates.'));
      },
    });
  }

  get availableCountries(): string[] {
    return Array.from(new Set(this.templates().map((t) => t.countryCode))).sort();
  }

  countryLabel(code: string): string {
    const country = findCountry(code);
    return country ? `${code} · ${country.name}` : code;
  }

  selectCountry(code: string): void {
    this.selectedCountry.set(code);
    this.result.set(null);
    this.docValues = {};
    this.events = [];
    const firstType = this.eventTypeOptions[0];
    if (firstType) {
      this.addEvent(firstType);
    }
  }

  setMode(mode: TesterMode): void {
    this.mode.set(mode);
    this.result.set(null);
    // A fragment is one event by definition; keep the first and drop the rest rather than
    // silently rendering only part of what the form still shows.
    if (mode === 'fragment' && this.events.length > 1) {
      this.events = this.events.slice(0, 1);
    }
  }

  get documentTemplate(): CountryEventTemplateResponse | undefined {
    return this.templates().find((t) => t.countryCode === this.selectedCountry() && t.scope === 'DOCUMENT');
  }

  get eventTemplates(): CountryEventTemplateResponse[] {
    return this.templates().filter((t) => t.countryCode === this.selectedCountry() && t.scope === 'EVENT');
  }

  get eventTypeOptions(): string[] {
    return this.eventTemplates
      .map((t) => t.eventType)
      .sort((a, b) => (a === '*' ? -1 : b === '*' ? 1 : a.localeCompare(b)));
  }

  eventTypeLabel(eventType: string): string {
    return eventType === '*' ? 'Every other type (wildcard)' : eventType;
  }

  /** Which document variables the country's envelope actually binds — no point typing the rest. */
  get documentFields(): string[] {
    const envelope = this.documentTemplate;
    if (!envelope) {
      return [];
    }
    try {
      const bound = new Set(analyse(envelope.template).scalars);
      return EDITABLE_DOCUMENT_VARIABLES.filter((name) => bound.has(name));
    } catch {
      return [];
    }
  }

  addEvent(eventType?: string): void {
    const type = eventType ?? this.eventTypeOptions[0];
    if (!type) {
      return;
    }
    this.events = [...this.events, { key: this.nextKey++, eventType: type, scalars: {}, lists: {} }];
    this.result.set(null);
  }

  removeEvent(key: number): void {
    this.events = this.events.filter((e) => e.key !== key);
    this.result.set(null);
  }

  onEventTypeChange(): void {
    this.result.set(null);
  }

  /** This event type's own template, or the country's wildcard one. */
  private templateFor(eventType: string): CountryEventTemplateResponse | undefined {
    const specific = this.eventTemplates.find((t) => t.eventType === eventType);
    return specific ?? this.eventTemplates.find((t) => t.eventType === '*');
  }

  fieldsFor(event: DraftEvent): { scalars: string[]; lists: string[] } {
    const template = this.templateFor(event.eventType);
    if (!template) {
      return { scalars: [], lists: [] };
    }
    try {
      return analyse(template.template);
    } catch {
      return { scalars: [], lists: [] };
    }
  }

  /** True when the country withholds this term, so the tester can grey the input out up front. */
  isWithheld(event: DraftEvent, term: string): boolean {
    return this.effectiveRules(event.eventType).get(term)?.onChain === false;
  }

  isRequired(event: DraftEvent, term: string): boolean {
    return this.effectiveRules(event.eventType).get(term)?.required === true;
  }

  /** The country's wildcard rules with any rule for this specific type layered on top. */
  private effectiveRules(eventType: string): Map<string, CountryTermRuleResponse> {
    const countryRules = this.rules().filter((r) => r.countryCode === this.selectedCountry());
    const effective = new Map<string, CountryTermRuleResponse>();
    for (const rule of countryRules) {
      if (rule.eventType === '*') {
        effective.set(rule.term, rule);
      }
    }
    for (const rule of countryRules) {
      if (rule.eventType === eventType) {
        effective.set(rule.term, rule);
      }
    }
    return effective;
  }

  generate(): void {
    const unresolved = new Set<string>();
    const withheld = new Set<string>();
    const missing = new Set<string>();
    const code = this.selectedCountry();

    try {
      const xml = this.build(unresolved, withheld, missing);

      // Checked after rendering, not before: which rules matter depends on the event types present.
      if (withheld.size > 0) {
        throw new Problem(
          'TERM_NOT_PUBLISHABLE',
          `${code} holds these terms at onChain=false, so the template cannot publish them: ${[...withheld].join(', ')}`
        );
      }
      if (missing.size > 0) {
        throw new Problem(
          'MISSING_REQUIRED_FIELD',
          `${code} requires these terms, and they were left empty: ${[...missing].join(', ')}`
        );
      }
      const wellFormed =
        this.mode() === 'document' ? isWellFormed(xml) : isWellFormedDocumentOrFragment(xml);
      if (!wellFormed) {
        throw new Problem('MALFORMED_XML', 'The rendered output does not parse as XML.');
      }

      this.result.set({
        xml,
        problemCode: null,
        problem: null,
        unresolved: [...unresolved].sort(),
        withheld: [],
      });
    } catch (e) {
      const isProblem = e instanceof Problem;
      const isTemplate = e instanceof TemplateError;
      this.result.set({
        xml: null,
        problemCode: isProblem ? e.code : isTemplate ? 'INVALID_TEMPLATE' : 'RENDER_FAILED',
        problem: e instanceof Error ? e.message : String(e),
        unresolved: [...unresolved].sort(),
        withheld: [...withheld].sort(),
      });
    }
  }

  private build(unresolved: Set<string>, withheld: Set<string>, missing: Set<string>): string {
    const code = this.selectedCountry();

    const fragments = this.events
      .map((event) => {
        const template = this.templateFor(event.eventType);
        if (!template) {
          throw new Problem(
            'NO_EVENT_TEMPLATE',
            `${code} has no XML template for ${event.eventType} and no wildcard (*) template to fall back on`
          );
        }
        const effective = this.effectiveRules(event.eventType);
        return render(template.template, (term) => {
          const rule = effective.get(term);
          if (rule && !rule.onChain) {
            withheld.add(term);
            return null;
          }
          const value =
            event.lists[term] !== undefined
              ? event.lists[term].split('\n').map((line) => line.trim()).filter(Boolean)
              : (event.scalars[term] ?? '').trim();
          const empty = Array.isArray(value) ? value.length === 0 : value === '';
          if (empty) {
            if (rule?.required) {
              missing.add(`${term} (${event.eventType})`);
            }
            unresolved.add(term);
            return null;
          }
          return value;
        });
      })
      .join('');

    if (this.mode() === 'fragment') {
      return fragments;
    }

    const envelope = this.documentTemplate;
    if (!envelope) {
      throw new Problem('NO_XML_TEMPLATE', `No XML document template is configured for ${code}.`);
    }

    return render(envelope.template, (term) => {
      if (term === 'events') {
        return new Raw(fragments);
      }
      if (term === 'eventCount') {
        return String(this.events.length);
      }
      if (term === 'countryCode') {
        return code;
      }
      const value = (this.docValues[term] ?? '').trim();
      if (!value) {
        unresolved.add(term);
        return null;
      }
      return value;
    });
  }

  copyXml(): void {
    const xml = this.result()?.xml;
    if (xml) {
      navigator.clipboard.writeText(xml);
    }
  }

  downloadXml(): void {
    const xml = this.result()?.xml;
    if (!xml) {
      return;
    }
    const blob = new Blob([xml], { type: 'application/xml;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${this.selectedCountry().toLowerCase()}-${this.mode()}.xml`;
    link.click();
    URL.revokeObjectURL(url);
  }
}
